/**
 * DeclaredStoreSkusSection: Settings, Printing, "Store SKUs for standard
 * products". The per-org store SKU switch (`productSync.getDeclaredSkuMode` /
 * `setDeclaredSkuMode`) and its read-only Verify (`verifyDeclaredSkus`).
 *
 * What the switch means (recognition only): with it on, product sync and
 * print time treat each set-up standard product's store SKU as its print SKU,
 * so standard products can get a print file per size. Nothing is ever written
 * to the store, in either direction.
 *
 * Shape (DESIGN.md, mirroring the Platen size section beside it):
 *   - The section renders its own `SettingsSection` (like `TencentCosSection`)
 *     so the current state sits in the header as a pill (section 13.1: emerald
 *     On, muted Off), with the words in the pill so colour is never the only
 *     signal.
 *   - Two radio options, Off and On, each with its consequence underneath.
 *     Choosing the other option never saves directly: it opens an AlertDialog
 *     for that direction, and the section saves through that confirm (as the
 *     Platen size section saves through its own dialog, not the header save).
 *   - Turning on loads `previewStoreSkuSwitchImpact` inside the dialog: how
 *     many set-up SKUs have a platen size rule but no per-size file. They stop
 *     printing until a file is uploaded. That is a warning, never a block:
 *     Confirm is disabled only while the count is loading or after it failed.
 *     A count of 0 says so ("No SKU will stop printing."), never silence.
 *   - With the switch on, the next step (set up products) links to the
 *     Products list filtered to the products that need set-up: a product
 *     changes only once it is set up there.
 *   - Turning off is reversible (section 13.6, orange outline confirm). Its body
 *     says the switch controls recognition only: per-size files already
 *     uploaded keep printing and are not removed.
 *   - A non-composition org (product sync already builds the print SKU) sees
 *     one plain line and no control, unless its switch is somehow still on, in
 *     which case the control stays so an admin can turn it off.
 *   - Coordinators see the state read-only; Verify is admins only (the
 *     procedures are admin only, RBAC four layers).
 *   - Below the switch, a composition org sees the read-only automatic
 *     set-up status (`StoreSkuAutoSetUp`, 88a.1): no control of its own. The
 *     switch's one `utils.productSync.invalidate()` refetches it.
 *   - Verify results are built from reason codes and counts only, translated
 *     here; mismatch rows show product title, size, colour and SKUs, never a
 *     variant id or a URL.
 */
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { STORE_SKUS_INTAKE_ON_MARKER, type DeclaredSkuMode } from '@threadheads/shared';
import { ArrowRight, Barcode, CheckCircle2, Clock, Loader2, ShieldCheck } from 'lucide-react';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { readCauseCode } from '@/lib/trpc-error-utils';
import { useRole } from '@/hooks/use-role';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionError } from '@/components/shared/SectionError';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { SettingsSection } from './console/SettingsSection';
import { VerifyFacet } from './VerifyFacet';
import { StoreSkuAutoSetUp } from './StoreSkuAutoSetUp';
import { WarningNotice } from '@/components/shared/WarningNotice';
import { StatusPill } from '@/components/shared/StatusPill';
import { NoteLine } from '@/components/shared/NoteLine';
import { storeSkuProductsHref } from '@/features/product-sync/lib/store-skus';

type VerifyReport = RouterOutputs['productSync']['verifyDeclaredSkus'];

/** The anchor `areas.ts` registers; deep links use `/production/settings?area=printing#store-skus`. */
const STORE_SKUS_SECTION_ID = 'store-skus';
/** The Code tables editor on the product sync page. */
const CODE_TABLES_PATH = '/product-sync?tab=code';
/** The next step once the switch is on: the Products list, filtered to the products that need set-up. */
const SET_UP_PRODUCTS_PATH = storeSkuProductsHref('needed');
const SET_MODE_ERROR_TOAST_ID = 'store-skus-set-mode-error';
const MODES: readonly DeclaredSkuMode[] = ['off', 'on'];

/** The tRPC error code (`PRECONDITION_FAILED`, `TOO_MANY_REQUESTS`, ...), if any. */
function readErrorCode(error: unknown): string | undefined {
  const data = (error as { data?: { code?: unknown; httpStatus?: unknown } } | null | undefined)?.data;
  if (typeof data?.code === 'string') return data.code;
  return data?.httpStatus === 429 ? 'TOO_MANY_REQUESTS' : undefined;
}

/**
 * The refusal for a non-composition org. The cause code is the contract, but
 * tRPC 11.7.1 can drop error causes, so any other PRECONDITION_FAILED counts
 * as the same refusal (the intake refusal below is matched first).
 */
function isNotSupportedError(error: unknown): boolean {
  return readCauseCode(error) === 'STORE_SKUS_NOT_SUPPORTED' || readErrorCode(error) === 'PRECONDITION_FAILED';
}

/**
 * `on` refused because the org already takes orders on a partner warehouse's
 * blanks (base SKU intake on). tRPC drops causes, so the message marker decides it.
 */
function isIntakeOnError(error: unknown): boolean {
  const message = (error as { message?: unknown } | null | undefined)?.message;
  return typeof message === 'string' && message.startsWith(`${STORE_SKUS_INTAKE_ON_MARKER}:`);
}

/**
 * `off` refused because product sync uses the org's own design IDs. The cause
 * code is the contract; tRPC can drop causes, and `off` has no other
 * PRECONDITION_FAILED refusal, so that code on an `off` save counts too.
 */
function isByoOnError(error: unknown, mode: DeclaredSkuMode): boolean {
  return readCauseCode(error) === 'STORE_SKUS_OFF_BYO_ON'
    || (mode === 'off' && readErrorCode(error) === 'PRECONDITION_FAILED');
}

const LINK_CLASS =
  'font-medium text-threadheads-purple underline underline-offset-2 hover:text-threadheads-purple/80 motion-reduce:transition-none';

export function DeclaredStoreSkusSection(): JSX.Element {
  const { t } = useTranslation('settings');
  const { isAdmin } = useRole();
  const admin = isAdmin();
  const utils = trpc.useUtils();
  const groupLabelId = useId();
  const adminOnlyId = useId();

  const modeQuery = trpc.productSync.getDeclaredSkuMode.useQuery(undefined, { staleTime: 30_000 });
  // The direction an open confirm dialog is about; null when none is open.
  const [confirming, setConfirming] = useState<DeclaredSkuMode | null>(null);

  // Counted fresh every time the turn-on dialog opens, never from a cache.
  const impactQuery = trpc.productSync.previewStoreSkuSwitchImpact.useQuery(undefined, {
    enabled: admin && confirming === 'on',
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const verifyMutation = trpc.productSync.verifyDeclaredSkus.useMutation();

  const setModeMutation = trpc.productSync.setDeclaredSkuMode.useMutation({
    onSuccess: (result) => {
      toast.dismiss(SET_MODE_ERROR_TOAST_ID);
      // The new state at once (the radio and the pill), then the server's.
      utils.productSync.getDeclaredSkuMode.setData(undefined, (prev) => (prev ? { ...prev, mode: result.mode } : prev));
      setConfirming(null);
      // A Verify result from before the change describes the old state.
      verifyMutation.reset();
      // Product sync's lists and Product updates follow the switch.
      void utils.productSync.invalidate();
      void utils.productUpdates.invalidate();
    },
    onError: (error, variables) => {
      setConfirming(null);
      const message = isIntakeOnError(error)
        ? t('storeSkus.intakeOnError')
        : isByoOnError(error, variables.mode)
          ? t('storeSkus.byoOnError')
          : isNotSupportedError(error)
            ? t('storeSkus.notSupportedError')
            : t('storeSkus.saveError');
      toast.error(message, {
        id: SET_MODE_ERROR_TOAST_ID,
        duration: Infinity,
      });
    },
  });

  const data = modeQuery.data;
  const mode: DeclaredSkuMode | null = data?.mode ?? null;
  // A non-composition org has nothing to change, unless its switch is on
  // (then it can still be turned off).
  const showControl = !!data && (data.compositionOrg || data.mode === 'on');
  const saving = setModeMutation.isPending;

  const chooseMode = (next: string): void => {
    if (!admin || saving || !mode || next === mode) return;
    if (next === 'on' || next === 'off') setConfirming(next);
  };

  const closeDialog = (open: boolean): void => {
    // A save in flight finishes first; its outcome closes the dialog.
    if (!open && !saving) setConfirming(null);
  };

  // ---- turn-on impact (S2: warn, never block) ----
  const impact = impactQuery.data;
  const impactLoading = impactQuery.isFetching || (impactQuery.isPending && !impactQuery.isError);
  const impactFailed = impactQuery.isError && !impactQuery.isFetching;
  const impactReady = !impactLoading && !impactFailed && !!impact;

  let impactBody: ReactNode = null;
  if (impactLoading) {
    impactBody = (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
        {t('storeSkus.turnOn.counting')}
      </p>
    );
  } else if (impactFailed) {
    impactBody = (
      <SectionError
        variant="inline"
        body={t('storeSkus.turnOn.countError')}
        onRetry={() => void impactQuery.refetch()}
        retryLabel={t('storeSkus.retry')}
        retrying={impactQuery.isFetching}
      />
    );
  } else if (impact && impact.ruledWithoutFile > 0) {
    impactBody = (
      <WarningNotice data-testid="store-skus-held-count">
        {impact.truncated
          ? t('storeSkus.turnOn.heldAtLeast', { count: impact.ruledWithoutFile })
          : t('storeSkus.turnOn.held', { count: impact.ruledWithoutFile })}
      </WarningNotice>
    );
  } else if (impact) {
    // The count came back 0: say the result, so the check never ends in
    // silence (the status region announces it).
    impactBody = (
      <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="store-skus-held-none">
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
        {t('storeSkus.turnOn.heldNone')}
      </p>
    );
  }

  // ---- header pill ----
  const headerPill =
    showControl && mode ? (
      <StatusPill role="status" tone={mode === 'on' ? 'on' : 'off'} srLabel={t('storeSkus.title')}>
        {mode === 'on' ? t('storeSkus.pillOn') : t('storeSkus.pillOff')}
      </StatusPill>
    ) : undefined;

  // ---- body ----
  let body: ReactNode;
  if (modeQuery.isError) {
    body = (
      <SectionError
        variant="compact"
        body={t('storeSkus.loadError')}
        onRetry={() => void modeQuery.refetch()}
        retryLabel={t('storeSkus.retry')}
        retrying={modeQuery.isFetching}
      />
    );
  } else if (!data || !mode) {
    body = (
      <div className="grid gap-2 sm:grid-cols-2" aria-busy="true">
        <span className="sr-only" role="status">
          {t('storeSkus.loading')}
        </span>
        <Skeleton className="h-20 w-full rounded-lg" />
        <Skeleton className="h-20 w-full rounded-lg" />
      </div>
    );
  } else if (!showControl) {
    body = <p className="max-w-prose text-sm text-muted-foreground">{t('storeSkus.notSupported')}</p>;
  } else {
    body = (
      <div className="space-y-4">
        <span id={groupLabelId} className="sr-only">
          {t('storeSkus.title')}
        </span>
        <RadioGroup
          value={mode}
          onValueChange={chooseMode}
          disabled={!admin || saving}
          aria-labelledby={groupLabelId}
          aria-describedby={admin ? undefined : adminOnlyId}
          className="grid gap-2 sm:grid-cols-2"
        >
          {MODES.map((option) => (
            <ModeOption key={option} option={option} disabled={!admin || saving} />
          ))}
        </RadioGroup>

        {!admin && (
          <p id={adminOnlyId} className="text-sm text-muted-foreground">
            {t('storeSkus.adminOnly')}
          </p>
        )}

        <NoteLine as="p" icon={Clock} className="max-w-prose">
          {t('storeSkus.timingNote')}
        </NoteLine>

        {/* On: nothing changes for a product until it is set up, so point at
            where that happens (the Products list, filtered to them). */}
        {mode === 'on' && data.compositionOrg && (
          <p className="text-sm">
            <Link to={SET_UP_PRODUCTS_PATH} className={cn(LINK_CLASS, 'inline-flex items-center gap-1')}>
              {t('storeSkus.nextSetUp')}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </p>
        )}

        {/* Read only, for anyone who can see this section (88a.1). */}
        {data.compositionOrg && <StoreSkuAutoSetUp compositionOrg={data.compositionOrg} />}

        {admin && <VerifyArea mutation={verifyMutation} />}
      </div>
    );
  }

  return (
    <SettingsSection
      id={STORE_SKUS_SECTION_ID}
      title={t('storeSkus.title')}
      description={t('storeSkus.description')}
      icon={Barcode}
      scope="org"
      headerRight={headerPill}
    >
      {body}

      {/* Turn on: the held count first (S2). Confirm waits for the count. */}
      <AlertDialog open={confirming === 'on'} onOpenChange={closeDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('storeSkus.turnOn.title')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>{t('storeSkus.turnOn.body')}</p>
                <div role="status" aria-live="polite">
                  {impactBody}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>{t('storeSkus.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90 motion-reduce:transition-none"
              disabled={!impactReady || saving}
              onClick={(event) => {
                event.preventDefault();
                if (!impactReady || saving) return;
                setModeMutation.mutate({ mode: 'on' });
              }}
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
              {saving ? t('storeSkus.turnOn.pending') : t('storeSkus.turnOn.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Turn off: reversible, so the confirm is orange outline (section 13.6). */}
      <AlertDialog open={confirming === 'off'} onOpenChange={closeDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('storeSkus.turnOff.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('storeSkus.turnOff.body')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>{t('storeSkus.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="gap-1.5 border border-orange-300 bg-background text-orange-700 hover:bg-orange-50 hover:text-orange-700 motion-reduce:transition-none"
              disabled={saving}
              onClick={(event) => {
                event.preventDefault();
                if (saving) return;
                setModeMutation.mutate({ mode: 'off' });
              }}
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
              {saving ? t('storeSkus.turnOff.pending') : t('storeSkus.turnOff.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsSection>
  );
}

// ---------------------------------------------------------------------------
// One radio option: the state, and what it means, as one clickable card.
// ---------------------------------------------------------------------------

function ModeOption({ option, disabled }: { option: DeclaredSkuMode; disabled: boolean }): JSX.Element {
  const { t } = useTranslation('settings');
  const id = useId();
  const labelId = `${id}-label`;
  const bodyId = `${id}-body`;
  // The whole card is the click target; the radio is named by the state alone
  // and described by its consequence.
  return (
    <label
      htmlFor={id}
      className={cn(
        'flex items-start gap-3 rounded-lg border bg-muted/30 p-3 transition-colors duration-200 ease-out motion-reduce:transition-none',
        'has-[[data-state=checked]]:border-threadheads-purple/60 has-[[data-state=checked]]:bg-threadheads-purple/[0.04]',
        disabled ? 'cursor-default' : 'cursor-pointer hover:bg-muted/50',
      )}
    >
      <RadioGroupItem value={option} id={id} aria-labelledby={labelId} aria-describedby={bodyId} className="mt-0.5" />
      <span className="min-w-0 space-y-1">
        <span id={labelId} className="block text-sm font-medium">
          {option === 'on' ? t('storeSkus.on.label') : t('storeSkus.off.label')}
        </span>
        <span id={bodyId} className="block text-xs leading-4 text-muted-foreground">
          {option === 'on' ? t('storeSkus.on.body') : t('storeSkus.off.body')}
        </span>
      </span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Verify (admins only): read-only checks, one facet per check.
// ---------------------------------------------------------------------------

type VerifyMutation = Pick<
  ReturnType<typeof trpc.productSync.verifyDeclaredSkus.useMutation>,
  'mutate' | 'isPending' | 'data' | 'error'
>;

function VerifyArea({ mutation }: { mutation: VerifyMutation }): JSX.Element {
  const { t } = useTranslation('settings');
  const report = mutation.data;
  const failure = mutation.error && !mutation.isPending ? mutation.error : null;

  return (
    <div className="space-y-3 border-t pt-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          className="h-8 gap-1.5 px-3 text-xs motion-reduce:transition-none"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          ) : (
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {t('storeSkus.verify.button')}
        </Button>
        <p className="max-w-prose text-xs text-muted-foreground">{t('storeSkus.verify.intro')}</p>
      </div>

      {/* Always mounted; only its content swaps, so screen readers announce
          the outcome (DESIGN.md section 16). */}
      <div role="status" aria-live="polite">
        {mutation.isPending ? (
          <span className="sr-only">{t('storeSkus.verify.running')}</span>
        ) : failure ? (
          <p className="text-xs text-destructive">
            {readErrorCode(failure) === 'TOO_MANY_REQUESTS' ? t('storeSkus.verify.tooMany') : t('storeSkus.verify.error')}
          </p>
        ) : report ? (
          <VerifyResults report={report} />
        ) : null}
      </div>
    </div>
  );
}

function VerifyResults({ report }: { report: VerifyReport }): JSX.Element {
  const { t } = useTranslation('settings');
  const sr = { srPassedLabel: t('storeSkus.verify.passed'), srFailedLabel: t('storeSkus.verify.needsAttention') };
  const has = (code: VerifyReport['reasons'][number]['code']): boolean => report.reasons.some((r) => r.code === code);
  const noCodeTables = has('no_code_tables');
  const shopifyUnavailable = report.sample.errorCode === 'shopify_unavailable';
  const mismatches = report.sample.mismatches;
  const heldKey = report.mode === 'on' ? 'storeSkus.verify.heldNow' : 'storeSkus.verify.heldIfOn';
  // Already on: the advice is to turn it off, not to keep it off.
  const baseSkuKey =
    report.mode === 'on' ? 'storeSkus.verify.declaredWithBaseSkuOn' : 'storeSkus.verify.declaredWithBaseSku';

  let storeLabel: string;
  if (report.declared === 0) storeLabel = t('storeSkus.verify.noneSetUp');
  else if (shopifyUnavailable) storeLabel = t('storeSkus.verify.shopifyUnavailable');
  else if (mismatches.length > 0) storeLabel = t('storeSkus.verify.mismatches', { count: mismatches.length });
  else storeLabel = t('storeSkus.verify.storeOk');

  return (
    <div className="space-y-2 rounded-md border p-3" data-testid="store-skus-verify-results">
      {has('not_composition_org') && <VerifyFacet ok={false} label={t('storeSkus.notSupported')} {...sr} />}

      <VerifyFacet
        ok={!noCodeTables}
        label={noCodeTables ? t('storeSkus.verify.noCodeTables') : t('storeSkus.verify.codeTablesOk')}
        {...sr}
      >
        {noCodeTables ? (
          <Link to={CODE_TABLES_PATH} className={LINK_CLASS}>
            {t('storeSkus.verify.openCodeTables')}
          </Link>
        ) : null}
      </VerifyFacet>

      <VerifyFacet
        ok={report.declaredWithBaseSku === 0}
        label={
          report.declaredWithBaseSku === 0
            ? t('storeSkus.verify.baseSkuOk')
            : t(baseSkuKey, { count: report.declaredWithBaseSku })
        }
        {...sr}
      />

      <VerifyFacet
        ok={report.ruledWithoutFile === 0}
        label={
          report.ruledWithoutFile === 0
            ? t('storeSkus.verify.ruledOk')
            : report.ruledWithoutFileTruncated
              ? t('storeSkus.verify.ruledWithoutFileAtLeast', { count: report.ruledWithoutFile })
              : t('storeSkus.verify.ruledWithoutFile', { count: report.ruledWithoutFile })
        }
        {...sr}
      >
        {report.ruledWithoutFile > 0 ? t(heldKey, { count: report.ruledWithoutFile }) : null}
      </VerifyFacet>

      <VerifyFacet ok={!shopifyUnavailable && mismatches.length === 0} label={storeLabel} {...sr}>
        {report.declared > 0 && !shopifyUnavailable ? (
          <span className="tabular-nums">
            {t('storeSkus.verify.checked', { checked: report.sample.checked, total: report.sample.of })}
          </span>
        ) : null}
      </VerifyFacet>

      {mismatches.length > 0 && !shopifyUnavailable && (
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">{t('storeSkus.verify.mismatchNextStep')}</p>
          <div className="max-h-64 overflow-auto rounded-md border">
            <table className="w-full text-xs">
              <caption className="sr-only">{t('storeSkus.verify.mismatchCaption')}</caption>
              <thead>
                <tr className="border-b bg-muted/50 text-left">
                  <th scope="col" className="px-3 py-1.5 font-semibold">{t('storeSkus.verify.colProduct')}</th>
                  <th scope="col" className="px-3 py-1.5 font-semibold">{t('storeSkus.verify.colSize')}</th>
                  <th scope="col" className="px-3 py-1.5 font-semibold">{t('storeSkus.verify.colColour')}</th>
                  <th scope="col" className="px-3 py-1.5 font-semibold">{t('storeSkus.verify.colSetUpSku')}</th>
                  <th scope="col" className="px-3 py-1.5 font-semibold">{t('storeSkus.verify.colStoreSku')}</th>
                </tr>
              </thead>
              <tbody>
                {mismatches.map((m, i) => (
                  <tr key={`${m.declaredSku}-${i}`} className="border-b align-top last:border-b-0">
                    <td className="px-3 py-1.5">
                      {m.reason === 'variant_missing' ? t('storeSkus.verify.variantMissing') : (m.productTitle ?? '-')}
                    </td>
                    <td className="px-3 py-1.5">{m.size ?? '-'}</td>
                    <td className="px-3 py-1.5">{m.colour ?? '-'}</td>
                    <td className="break-all px-3 py-1.5 font-mono">{m.declaredSku}</td>
                    <td className="break-all px-3 py-1.5 font-mono">{m.liveSku ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {report.healthy && <p className="pt-0.5 text-xs text-emerald-700">{t('storeSkus.verify.healthy')}</p>}
    </div>
  );
}
